/**
 * Post-commit result sections for the merged "Zarezerwuj wstępnie" dialog —
 * extracted out of reserve-preliminary-dialog.tsx to keep that file under
 * this repo's 400-line limit. Kept the reserved half's outcomes visually
 * separate from the freed half's — both use the word "failed" for unrelated
 * reasons (offer-accept-reservation).
 */
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { InlineErrorMessage } from '@ooh/ui/components/inline-error-message';
import type { LockReservationsResponse, ReleaseRejectedUnitsResponse } from '@ooh/contracts';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';
import {
  OutcomeResultBucket,
  OutcomeResultEntryRow,
  outcomeFailedDetail,
  type LooseT,
  type OutcomeResultUnitLookup,
} from '../../reservations/components/outcome-result-buckets';
import {
  OutcomeResultView,
  type OutcomeResultStatConfig,
} from '../../reservations/components/outcome-result-view';

function activateUnit(
  onNavigateToUnit: ((mediaUnitId: number) => void) | undefined,
  mediaUnitId: number,
) {
  return onNavigateToUnit ? () => onNavigateToUnit(mediaUnitId) : undefined;
}

export function ReserveResultSection({
  result,
  lookup,
  onNavigateToUnit,
}: {
  result: PromiseSettledResult<LockReservationsResponse>;
  lookup: OutcomeResultUnitLookup;
  onNavigateToUnit?: (mediaUnitId: number) => void;
}) {
  const { t } = useTranslation('offers');
  const unknownUnitLabel = t('workspace.versions.lock.result.unknownUnit');
  const title = t('workspace.header.reservePreliminaryDialog.resultReserveTitle');

  if (result.status === 'rejected') {
    return (
      <div className="space-y-3" data-testid="reserve-preliminary-result-reserve">
        <h3 className="text-sm font-semibold">{title}</h3>
        <InlineErrorMessage
          tone="destructive"
          icon={XCircle}
          testId="reserve-preliminary-result-reserve-failed"
        >
          {t('workspace.header.reservePreliminaryDialog.resultReserveFailed')}
        </InlineErrorMessage>
      </div>
    );
  }
  const lockResult = result.value;
  const stats: OutcomeResultStatConfig[] = [
    {
      icon: CheckCircle2,
      tone: 'success',
      label: t('workspace.versions.lock.result.lockedLabel'),
      value: lockResult.locked.length,
      testId: 'lock-result-locked-count',
    },
    {
      icon: Clock,
      tone: 'warning',
      label: t('workspace.versions.lock.result.queuedLabel'),
      value: lockResult.queued.length,
      testId: 'lock-result-queued-count',
    },
    {
      icon: XCircle,
      tone: 'destructive',
      label: t('workspace.versions.lock.result.failedLabel'),
      value: lockResult.failed.length,
      testId: 'lock-result-failed-count',
    },
  ];

  return (
    <OutcomeResultView
      testId="reserve-preliminary-result-reserve"
      title={title}
      className="grid-cols-3"
      stats={stats}
    >
      <OutcomeResultBucket
        trigger={
          <span className="text-sm font-medium">
            {t('workspace.versions.lock.result.lockedLabel')}
          </span>
        }
        entries={lockResult.locked}
        emptyLabel={t('workspace.versions.lock.result.lockedEmpty')}
        testIdPrefix="lock-outcome-entry"
        renderEntry={(entry) => (
          <OutcomeResultEntryRow
            unit={lookup.get(entry.mediaUnitId)}
            dateFrom={entry.dateFrom}
            dateTo={entry.dateTo}
            detail={
              entry.ttlExpiresAt
                ? t('workspace.versions.lock.result.entryExpiresOn', {
                    date: formatDisplayDate(entry.ttlExpiresAt),
                  })
                : undefined
            }
            onActivate={activateUnit(onNavigateToUnit, entry.mediaUnitId)}
            unknownUnitLabel={unknownUnitLabel}
          />
        )}
      />
      <OutcomeResultBucket
        trigger={
          <span className="text-sm font-medium">
            {t('workspace.versions.lock.result.queuedLabel')}
          </span>
        }
        entries={lockResult.queued}
        emptyLabel={t('workspace.versions.lock.result.queuedEmpty')}
        testIdPrefix="lock-outcome-entry"
        renderEntry={(entry) => (
          <OutcomeResultEntryRow
            unit={lookup.get(entry.mediaUnitId)}
            dateFrom={entry.dateFrom}
            dateTo={entry.dateTo}
            detail={t('workspace.versions.lock.result.entryQueuedPosition', {
              position: entry.position,
            })}
            onActivate={activateUnit(onNavigateToUnit, entry.mediaUnitId)}
            unknownUnitLabel={unknownUnitLabel}
          />
        )}
      />
      <OutcomeResultBucket
        trigger={
          <span className="text-sm font-medium">
            {t('workspace.versions.lock.result.failedLabel')}
          </span>
        }
        entries={lockResult.failed}
        emptyLabel={t('workspace.versions.lock.result.failedEmpty')}
        testIdPrefix="lock-outcome-entry"
        renderEntry={(entry) => (
          <OutcomeResultEntryRow
            unit={lookup.get(entry.mediaUnitId)}
            dateFrom={entry.dateFrom}
            dateTo={entry.dateTo}
            detail={outcomeFailedDetail(entry, t as LooseT)}
            onActivate={activateUnit(onNavigateToUnit, entry.mediaUnitId)}
            unknownUnitLabel={unknownUnitLabel}
          />
        )}
      />
    </OutcomeResultView>
  );
}

export function ReleaseResultSection({
  result,
  lookup,
}: {
  result: PromiseSettledResult<ReleaseRejectedUnitsResponse>;
  lookup: OutcomeResultUnitLookup;
}) {
  const { t } = useTranslation('offers');
  const unknownUnitLabel = t('workspace.versions.unitDecisions.release.result.unknownUnit');
  const looseT = t as LooseT;
  const title = t('workspace.header.reservePreliminaryDialog.resultReleaseTitle');

  if (result.status === 'rejected') {
    return (
      <div className="space-y-3" data-testid="reserve-preliminary-result-release">
        <h3 className="text-sm font-semibold">{title}</h3>
        <InlineErrorMessage
          tone="destructive"
          icon={XCircle}
          testId="reserve-preliminary-result-release-failed"
        >
          {t('workspace.header.reservePreliminaryDialog.resultReleaseFailed')}
        </InlineErrorMessage>
      </div>
    );
  }
  const releaseResult = result.value;
  const stats: OutcomeResultStatConfig[] = [
    {
      icon: CheckCircle2,
      tone: 'success',
      label: t('workspace.versions.unitDecisions.release.result.releasedLabel'),
      value: releaseResult.released.length,
      testId: 'release-result-released-count',
    },
    {
      icon: Clock,
      tone: 'neutral',
      label: t('workspace.versions.unitDecisions.release.result.nothingToReleaseLabel'),
      value: releaseResult.nothingToRelease.length,
      testId: 'release-result-nothing-to-release-count',
    },
    {
      icon: XCircle,
      tone: 'destructive',
      label: t('workspace.versions.unitDecisions.release.result.failedLabel'),
      value: releaseResult.failed.length,
      testId: 'release-result-failed-count',
    },
  ];

  return (
    <OutcomeResultView
      testId="reserve-preliminary-result-release"
      title={title}
      className="grid-cols-3"
      stats={stats}
    >
      <OutcomeResultBucket
        trigger={
          <span className="text-sm font-medium">
            {t('workspace.versions.unitDecisions.release.result.releasedLabel')}
          </span>
        }
        entries={releaseResult.released}
        emptyLabel={t('workspace.versions.unitDecisions.release.result.releasedEmpty')}
        testIdPrefix="release-outcome-entry"
        renderEntry={(entry) => (
          <OutcomeResultEntryRow
            unit={lookup.get(entry.mediaUnitId)}
            unknownUnitLabel={unknownUnitLabel}
          />
        )}
      />
      <OutcomeResultBucket
        trigger={
          <span className="text-sm font-medium">
            {t('workspace.versions.unitDecisions.release.result.nothingToReleaseLabel')}
          </span>
        }
        entries={releaseResult.nothingToRelease}
        emptyLabel={t('workspace.versions.unitDecisions.release.result.nothingToReleaseEmpty')}
        testIdPrefix="release-outcome-entry"
        renderEntry={(entry) => (
          <OutcomeResultEntryRow
            unit={lookup.get(entry.mediaUnitId)}
            unknownUnitLabel={unknownUnitLabel}
          />
        )}
      />
      <OutcomeResultBucket
        trigger={
          <span className="text-sm font-medium">
            {t('workspace.versions.unitDecisions.release.result.failedLabel')}
          </span>
        }
        entries={releaseResult.failed}
        emptyLabel={t('workspace.versions.unitDecisions.release.result.failedEmpty')}
        testIdPrefix="release-outcome-entry"
        renderEntry={(entry) => (
          <OutcomeResultEntryRow
            unit={lookup.get(entry.mediaUnitId)}
            detail={outcomeFailedDetail(entry, looseT)}
            unknownUnitLabel={unknownUnitLabel}
          />
        )}
      />
    </OutcomeResultView>
  );
}
